import * as React from 'react'

import { Cell, Row, RowData, Table } from '@tanstack/react-table'
import { useVirtualizer } from '@tanstack/react-virtual'

import Localization from '../../localization/LocalizationManager'
import { cn } from '../../utils/cn'
import BodyCell from './BodyCell'
import {
  computeSizeVariables,
  DEFAULT_COLUMN_MIN_SIZE,
  hasUnsupportedGroupedHeaders,
  UNCAPPED_COLUMN_SIZE,
} from './columns'
import HeaderCell from './HeaderCell'
import { STYLES, UNSUPPORTED_GROUPED_HEADER_MESSAGE } from './styles'
import useFillContainerWidth from './useFillContainerWidth'

const DEFAULT_OVERSCAN = 5
const DEFAULT_ROW_HEIGHT = 40

interface ISharedVirtualTableProps<TData extends RowData> {
  table: Table<TData>
  ariaLabel: string
  className?: string
  overscan?: number
  estimateRowHeight?: (row: Row<TData>) => number
  getRowClassName?: (row: Row<TData>) => string | undefined
  onRowClick?: (row: Row<TData>, event: React.MouseEvent) => void
  onCellClick?: (cell: Cell<TData, unknown>) => void
  renderRow?: (
    row: Row<TData>,
    defaultCells: React.ReactNode[],
  ) => React.ReactNode
  emptyState?: React.ReactNode
  fillContainerWidth?: boolean
  columnWidths?: Record<string, number>
  onColumnWidthsChange?: (next: Record<string, number>) => void
}

export default function SharedVirtualTable<TData extends RowData>({
  table,
  ariaLabel,
  className,
  overscan = DEFAULT_OVERSCAN,
  estimateRowHeight,
  getRowClassName,
  onRowClick,
  onCellClick,
  renderRow,
  emptyState,
  fillContainerWidth = false,
  columnWidths,
  onColumnWidthsChange,
}: ISharedVirtualTableProps<TData>): React.ReactElement {
  const scrollRef = React.useRef<HTMLDivElement>(null)
  const rows = table.getRowModel().rows
  const headerGroups = table.getHeaderGroups()
  const visibleColumns = table.getVisibleLeafColumns()
  const allLeafColumns = table.getAllLeafColumns()
  const flatHeaders = table.getFlatHeaders()

  // The estimate is used as the row/cell minHeight floor. It must NOT be the
  // measured virtualRow.size — pinning minHeight to the live measurement
  // ratchets the row taller and prevents it from shrinking when its content
  // (e.g. an expanded tag list) collapses again.
  const getEstimatedSize = React.useCallback(
    (row: Row<TData>) =>
      estimateRowHeight ? estimateRowHeight(row) : DEFAULT_ROW_HEIGHT,
    [estimateRowHeight],
  )

  const rowVirtualizer = useVirtualizer({
    count: rows.length,
    getScrollElement: () => scrollRef.current,
    estimateSize: index => getEstimatedSize(rows[index]),
    getItemKey: index => rows[index]?.id ?? index,
    overscan,
  })

  // The single clamped write path for deliberate column widths. Both the
  // keyboard resize (HeaderCell) and the end-of-drag fold (useFillContainerWidth)
  // funnel through here, so the clamp and the persisted shape live in one place
  // instead of being re-implemented per call site. Undefined when the consumer
  // doesn't own width state — callers then fall back to transient columnSizing.
  const setColumnWidth = React.useCallback(
    (columnId: string, width: number) => {
      if (!onColumnWidthsChange) return
      const column = table.getColumn(columnId)
      const min = column?.columnDef.minSize ?? DEFAULT_COLUMN_MIN_SIZE
      const max = column?.columnDef.maxSize ?? UNCAPPED_COLUMN_SIZE
      onColumnWidthsChange({
        ...(columnWidths ?? {}),
        [columnId]: Math.max(min, Math.min(max, width)),
      })
    },
    [table, columnWidths, onColumnWidthsChange],
  )
  const persistColumnWidth = onColumnWidthsChange ? setColumnWidth : undefined

  useFillContainerWidth(table, scrollRef, {
    enabled: fillContainerWidth,
    columnWidths,
    setColumnWidth: persistColumnWidth,
  })

  const { columnPinning, columnSizing } = table.getState()
  const sizeVars = React.useMemo(
    () => computeSizeVariables(visibleColumns, flatHeaders),
    [flatHeaders, visibleColumns, columnPinning, columnSizing],
  )

  if (allLeafColumns.length === 0 || visibleColumns.length === 0) {
    return (
      <div className={cn(STYLES.emptyStateContainer, className)}>
        <span className={STYLES.emptyStateText}>
          {Localization.translator.noColumnsDefined}
        </span>
      </div>
    )
  }

  if (hasUnsupportedGroupedHeaders(headerGroups)) {
    return (
      <div className={cn(STYLES.emptyStateContainer, className)}>
        <span className={STYLES.emptyStateText}>
          {UNSUPPORTED_GROUPED_HEADER_MESSAGE}
        </span>
      </div>
    )
  }

  const emptyContent = emptyState ?? (
    <div
      className={STYLES.emptyStateContent}
      ref={el => {
        if (!el) return
        const container = scrollRef.current
        if (container) {
          el.style.width = `${container.clientWidth}px`
        }
      }}
    >
      <span className={STYLES.emptyStateText}>
        {Localization.translator.noDataAvailable}
      </span>
    </div>
  )

  return (
    <div
      ref={scrollRef}
      role="grid"
      aria-label={ariaLabel}
      aria-rowcount={headerGroups.length + rows.length}
      aria-colcount={visibleColumns.length}
      className={cn(STYLES.gridContainer, className)}
      style={{
        contain: 'strict',
        overflowAnchor: 'none',
        ...sizeVars,
      }}
    >
      <div
        className={STYLES.innerTable}
        style={{ width: table.getTotalSize() }}
      >
        <div role="rowgroup" className={STYLES.headerRowGroup}>
          {headerGroups.map(headerGroup => (
            <div
              key={headerGroup.id}
              role="row"
              aria-rowindex={headerGroup.depth + 1}
              className={STYLES.headerRow}
            >
              {headerGroup.headers.map(header => (
                <HeaderCell
                  key={header.id}
                  header={header}
                  table={table}
                  fillContainerWidth={fillContainerWidth}
                  setColumnWidth={persistColumnWidth}
                />
              ))}
            </div>
          ))}
        </div>

        {rows.length === 0 ? (
          <div role="rowgroup">{emptyContent}</div>
        ) : (
          <div
            role="rowgroup"
            className="tw:relative"
            style={{ height: rowVirtualizer.getTotalSize() }}
          >
            {rowVirtualizer.getVirtualItems().map(virtualRow => {
              const row = rows[virtualRow.index]
              const estimatedSize = getEstimatedSize(row)

              const cells = row
                .getVisibleCells()
                .map(cell => (
                  <BodyCell
                    key={cell.id}
                    cell={cell}
                    minRowHeight={estimatedSize}
                    onCellClick={onCellClick}
                  />
                ))

              return (
                <div
                  key={String(virtualRow.key)}
                  data-index={virtualRow.index}
                  role="row"
                  aria-rowindex={headerGroups.length + virtualRow.index + 1}
                  className={cn(
                    STYLES.bodyRow,
                    !!onRowClick && STYLES.bodyRowClickable,
                    getRowClassName?.(row),
                  )}
                  style={{
                    minHeight: estimatedSize,
                    top: virtualRow.start,
                  }}
                  ref={rowVirtualizer.measureElement}
                  onClick={event => onRowClick?.(row, event)}
                >
                  {renderRow ? renderRow(row, cells) : cells}
                </div>
              )
            })}
          </div>
        )}
      </div>
    </div>
  )
}
